import React, { useEffect, useState, useRef } from 'react';
import { Marker, Tooltip, Popup } from 'react-leaflet';
import L from 'leaflet';
import Papa from 'papaparse';
import { parseSeededFieldDatalog, getLandmineData, getAIUColor, WebMapData } from '../../utils/aiuParse';
import './landmine.css'; // Import your custom styles

// Interface for landmine data matching your CSV structure
interface LandmineData {
  ID: string;
  Longitude: number;
  Latitude: number;
  Item: string;
  Class: string;
  'Depth (cm)': number | string;
  'Weight (g)': number | string;
  'Length (cm)': number | string;
  'Diameter (cm)': number | string;
  'Ferrous (1) or NF (0)': string | number;
  Country: string;
  Reference: string;
  Notes: string;
}

// Create custom icons based on AIU Index
const createLandmineIcon = (aiuIndex: number | undefined) => {
  const color = getAIUColor(aiuIndex);

  return L.divIcon({
    html: `<svg height="24" width="24" viewBox="0 0 100 100">
             <circle cx="50" cy="50" r="40" stroke="#000" stroke-width="6" fill="${color}" />
             <circle cx="50" cy="50" r="15" fill="#000" />
           </svg>`,
    className: "landmine-marker",
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
};

// Component for displaying landmine image from S3
const LandmineImage: React.FC<{ landmineId: string }> = React.memo(({ landmineId }) => {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageError, setImageError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Construct URL based on landmine ID
    const s3BaseUrl = process.env.REACT_APP_S3_BASE_URL || 'https://your-bucket.s3.region.amazonaws.com';

    // Use .jpg for local development, .webp for production/S3
    const isLocal = s3BaseUrl.includes('localhost') || s3BaseUrl.includes('0.0');
    const extension = isLocal ? 'jpg' : 'webp';
    const constructedUrl = `${s3BaseUrl}/landmine_images/${landmineId}.${extension}`;

    setImageUrl(constructedUrl);
    setLoading(true);
    setImageError(false);
  }, [landmineId]);

  const handleImageLoad = () => {
    setLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setLoading(false);
    setImageError(true);
  };
  console.log(`Rendering image for landmine ID: ${landmineId} from URL: ${imageUrl}`);

  return (
    <div className="landmine-image-container">
      {loading && <div className="landmine-image-loading">Loading landmine image...</div>}

      {imageUrl && (
        <img
          src={imageUrl}
          alt={`Landmine ${landmineId}`}
          className="landmine-image"
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            maxWidth: '200px',
            maxHeight: '150px',
            objectFit: 'cover',
            display: loading ? 'none' : 'block'
          }}
        />
      )}

      {imageError && <div className="landmine-image-error">Image not available</div>}
    </div>
  );
});

// Custom popup component with enhanced styling
const LandminePopup: React.FC<{ landmine: LandmineData; aiuIndex?: number; trueDepth?: number; notes?: string | number }> = ({ landmine, aiuIndex, trueDepth, notes }) => {
  const ferrous = landmine['Ferrous (1) or NF (0)'];
  const isFerrous = ferrous === 1 || ferrous === '1';
  const aiuColor = getAIUColor(aiuIndex);

  return (
    <div className="landmine-popup">
      <div className="landmine-header">
        <h3>{landmine.ID} {landmine.Item}</h3>
        <div className="badge-container">
          <span className={`ferrous-badge ${isFerrous ? 'ferrous' : 'non-ferrous'}`}>
            {isFerrous ? 'Ferrous' : 'Non-Ferrous'}
          </span>
          {aiuIndex !== undefined && (
            <span className="aiu-badge" style={{ backgroundColor: aiuColor, color: '#000' }}>
              AIU: {aiuIndex}
            </span>
          )}
        </div>
      </div>

      <LandmineImage landmineId={landmine.ID} />

      <div className="landmine-details">
        {landmine.Class && (
          <div className="detail-row">
            <strong>Class:</strong> {landmine.Class}
          </div>
        )}

        <div className="detail-grid">
          <div className="detail-item">
            <strong>Initial Depth:</strong> {
              landmine['Depth (cm)'] ? `${landmine['Depth (cm)']} cm` : 'Surface'}
          </div>
          {trueDepth !== undefined && (
            <div className="detail-item">
              <strong>True Depth:</strong> {trueDepth} cm
            </div>
          )}
          {landmine['Weight (g)'] && (
            <div className="detail-item">
              <strong>Weight:</strong> {landmine['Weight (g)']} g
            </div>
          )}
          {landmine['Length (cm)'] && (
            <div className="detail-item">
              <strong>Length:</strong> {landmine['Length (cm)']} cm
            </div>
          )}
          {landmine['Diameter (cm)'] && (
            <div className="detail-item">
              <strong>Diameter:</strong> {landmine['Diameter (cm)']} cm
            </div>
          )}
        </div>

        {landmine.Country && (
          <div className="detail-row">
            <strong>Country:</strong> {landmine.Country}
          </div>
        )}

        {landmine.Reference && (
          <div className="detail-row">
            <strong>Reference:</strong> {landmine.Reference}
          </div>
        )}

        {landmine.Notes && (
          <div className="detail-row notes">
            <strong>Notes:</strong> {landmine.Notes}
          </div>
        )}
        {notes && (
          <div className="detail-row notes">
            <strong>AIU Notes:</strong> {notes}
          </div>
        )}
      </div>
    </div>
  );
};

interface LandmineMarkersProps {
  selectedField?: number;
  visible?: boolean;
  layerId?: string; // Dataset ID to use for AIU data lookup
}

const LandmineMarkers: React.FC<LandmineMarkersProps> = ({
  selectedField,
  visible = true,
  layerId
}) => {
  const [landmineData, setLandmineData] = useState<LandmineData[]>([]);
  const [aiuData, setAiuData] = useState<WebMapData>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [currentTooltip, setCurrentTooltip] = useState<string | null>(null);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const markerRefs = useRef<{ [key: string]: any }>({});

  // Close all tooltips except the specified one
  const closeAllTooltipsExcept = (exceptId?: string) => {
    Object.entries(markerRefs.current).forEach(([markerId, marker]) => {
      if (markerId !== exceptId && marker) {
        marker.closeTooltip();
      }
    });
  };

  useEffect(() => {
    const loadLandmineData = async () => {
      try {
        setLoading(true);
        setError('');

        // Load AIU data from All_datasets.csv
        try {
          const aiuResponse = await fetch('/data/Seeded_Field_Datalog - Analysis_All_datasets.csv');
          if (aiuResponse.ok) {
            const aiuCsvContent = await aiuResponse.text();
            const parsedAiuData = parseSeededFieldDatalog(aiuCsvContent);
            setAiuData(parsedAiuData);
            console.log('AIU data loaded:', Object.keys(parsedAiuData));
          }
        } catch (aiuError) {
          console.warn('Could not load AIU data:', aiuError);
        }


        // Option 1: Load from public folder (recommended for static data)
        // const response = await fetch(`/data/{selectedField}.csv`);
        // if (!response.ok) {
        //   throw new Error(`HTTP error! status: ${response.status}`);
        // }
        // const csvContent = await response.text();

        //Option 2: Load from your existing tile server (uncomment if using this approach)
        const tileServerUrl = process.env.REACT_APP_S3_BASE_URL || 'http://localhost:8000';
        console.log(`Loading landmine data from: ${tileServerUrl}/site_csv/${selectedField}.csv`);
        const response = await fetch(`${tileServerUrl}/site_csv/${selectedField}.csv`);
        const csvContent = await response.text();

        console.log('CSV content loaded:', csvContent); // Log first 100 characters for debugging
        const result = Papa.parse<LandmineData>(csvContent, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          delimiter: ',',
          transformHeader: (header) => header.trim(), // Remove whitespace from headers
        });

        if (result.errors.length > 0) {
          console.warn('CSV parsing errors:', result.errors);
        }

        // Filter out invalid entries
        const validData = result.data.filter(item =>
          item.ID &&
          typeof item.Latitude === 'number' &&
          typeof item.Longitude === 'number' &&
          !isNaN(item.Latitude) &&
          !isNaN(item.Longitude)
        );

        setLandmineData(validData);
        console.log(`Loaded ${validData.length} landmine markers`);
      } catch (error) {
        console.error('Error loading landmine data:', error);
        setError('Failed to load landmine data');
      } finally {
        setLoading(false);
      }
    };

    loadLandmineData();
  }, [selectedField]);

  // Clean up tooltip state when landmine data changes
  useEffect(() => {
    setCurrentTooltip(null);
    markerRefs.current = {};
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
  }, [landmineData]);

  // Filter data by selected field if needed
  const filteredData = React.useMemo(() => {
    let baseData = landmineData;

    return baseData.filter(landmine => {
      const { ID, Latitude, Longitude, ...remainingData } = landmine;

      const hasMeaninfulData = Object.values(remainingData).some(value =>
        value !== null &&
        value !== undefined &&
        value !== '' &&
        value !== 0
      );
      return hasMeaninfulData;
    });
  }, [landmineData, selectedField]);

  if (!visible) return null;

  if (loading) {
    return null; // Or a loading indicator if desired
  }

  if (error) {
    console.error('LandmineMarkers error:', error);
    return null;
  }

  return (
    <>
      {filteredData.map((landmine) => {
        // Get AIU data for this landmine if layerId is provided
        const landmineAiuData = layerId ? getLandmineData(aiuData, layerId, landmine.ID) : undefined;
        const aiuIndex = landmineAiuData?.aiuIndex;
        const trueDepth = landmineAiuData?.trueDepth;
        console.log("Landmine AIU Data: ", landmineAiuData)
        const aiuNotes = landmineAiuData?.notes;

        return (
          <Marker
            key={landmine.ID}
            position={[landmine.Latitude, landmine.Longitude]}
            icon={createLandmineIcon(aiuIndex)}
            ref={(ref) => {
              if (ref) {
                markerRefs.current[landmine.ID] = ref;
              }
            }}
            eventHandlers={{
              mouseover: (e) => {
                // Clear any existing timeout
                if (tooltipTimeoutRef.current) {
                  clearTimeout(tooltipTimeoutRef.current);
                  tooltipTimeoutRef.current = null;
                }

                // Close all other tooltips
                closeAllTooltipsExcept(landmine.ID);

                // Set current tooltip and open it
                setCurrentTooltip(landmine.ID);
                e.target.openTooltip();
              },
              mouseout: (e) => {
                // Close tooltip immediately and clear state after a small delay
                e.target.closeTooltip();
                tooltipTimeoutRef.current = setTimeout(() => {
                  setCurrentTooltip(null);
                }, 100);
              }
            }}
          >
            <Tooltip
              direction="top"
              offset={[0, -10]}
              opacity={0.9}
              permanent={false}
              sticky={false}
            >
              <div className="landmine-tooltip">
                <strong>{landmine.ID}</strong><br />
                {landmine.Item}
                {aiuIndex !== undefined && (<><br />AIU: {aiuIndex}</>)}
              </div>
            </Tooltip>

            <Popup
              maxWidth={300}
              className="landmine-popup-container"
              closeOnEscapeKey={true}
              closeOnClick={true}
              autoPan={false}>
              <LandminePopup
                landmine={landmine}
                aiuIndex={aiuIndex}
                trueDepth={trueDepth}
                notes={aiuNotes}
              />
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

export default LandmineMarkers;
